import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {FC, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import cancelIcon from '../../assets/demo-registration/cancel.svg';
import contactInfoIcon from '../../assets/demo-registration/contact-info.svg';
import emailIcon from '../../assets/demo-registration/email.svg';
import generalInfoIcon from '../../assets/demo-registration/general-info.svg';
import websiteIcon from '../../assets/demo-registration/website.svg';
import {publicApi, UserErrorMessage} from '../../global/api';
import {DEFAULT_VALUES, EMAIL_PATTERN} from './constants';
import styles from './DemoRegistration.module.scss';
import {DemoCaptchaSolution, DemoRegistrationFormValues} from './types';
import {buildCreateForDemoPayload, isValidWebsite} from './utils';
import {FormPhoneField} from './ui/FormPhoneField';
import {FormTextField} from './ui/FormTextField';
import {SectionHeader} from './ui/SectionHeader';
import {CaptchaDialog} from './ui/CaptchaDialog';
import {validateInn} from '../../shared/validation/innValidation';

const SUCCESS_TEXT = 'Логин и пароль для входа в систему направлены на указанный электронный адрес';
const COMPANY_ALREADY_EXISTS_CODE = '4013';

const DemoRegistration: FC = () => {
  const navigate = useNavigate();
  const [pendingValues, setPendingValues] = useState<DemoRegistrationFormValues | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: {isSubmitting},
  } = useForm<DemoRegistrationFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const onSubmit: SubmitHandler<DemoRegistrationFormValues> = (values) => {
    setPendingValues(values);
  };

  const confirmRegistration = async (solution: DemoCaptchaSolution) => {
    if (!pendingValues) return;

    try {
      await publicApi.companies.postCompaniesCreateForDemo({
        ...buildCreateForDemoPayload(pendingValues),
        ...solution,
      });
    } catch (error) {
      const data = isAxiosError<UserErrorMessage>(error) ? error.response?.data : undefined;
      if (data?.code !== COMPANY_ALREADY_EXISTS_CODE) throw error;

      setPendingValues(null);
      toast.error(data.message || 'Компания уже существует', {containerId: 'default'});
      return;
    }

    toast.success(`${SUCCESS_TEXT}: ${pendingValues.email}`, {
      containerId: 'default',
      autoClose: false,
      hideProgressBar: true,
      pauseOnHover: false,
      draggable: false,
      closeOnClick: true,
    });
    setPendingValues(null);
    reset(DEFAULT_VALUES);
    navigate('/login');
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Регистрация нового клиента</h1>
        <p className={styles.subtitle}>Создайте профиль организации и начните работу</p>

        <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
          <section className={styles.section}>
            <SectionHeader icon={generalInfoIcon} title="Общая информация" required />
            <FormTextField
              control={control}
              name="fullCompanyName"
              label="Наименование организации"
              placeholder="Организационно-правовая форма (ООО, ИП), наименование компании"
              required
              rules={{
                required: 'Укажите наименование организации',
                validate: (value) => value.trim().length > 0 || 'Укажите наименование организации',
              }}
            />
            <div className={styles.fieldGridSingle}>
              <FormTextField
                control={control}
                name="inn"
                label="ИНН компании"
                required
                placeholder="1234567890"
                rules={{
                  required: 'Укажите ИНН компании',
                  pattern: {
                    value: /^(\d{10})?(\d{12})?$/,
                    message: '10 или 12 цифр',
                  },
                  validate: {
                    validateInn: (value) => validateInn(value),
                  },
                }}
                transformValue={(value) => value.replace(/\D/g, '').slice(0, 12)}
              />
            </div>
          </section>

          <section className={styles.section}>
            <SectionHeader icon={contactInfoIcon} title="Контактная информация" />
            <div className={styles.fieldGrid}>
              <FormPhoneField control={control} />
              <FormTextField
                control={control}
                name="email"
                label="Адрес электронной почты"
                placeholder="info@example.ru"
                required
                startIcon={emailIcon}
                rules={{
                  required: 'Укажите адрес электронной почты',
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: 'Укажите корректный email',
                  },
                }}
              />
              <FormTextField
                control={control}
                name="website"
                label="Сайт компании"
                placeholder="www.example.ru"
                startIcon={websiteIcon}
                rules={{
                  validate: (value) => isValidWebsite(value) || 'Укажите корректный адрес сайта',
                }}
              />
            </div>
          </section>

          <div className={styles.actions}>
            <Button
              type="button"
              variant="outlined"
              className={styles.cancelButton}
              startIcon={<img src={cancelIcon} alt="" className={styles.cancelIcon} />}
              onClick={() => {
                if (document.referrer && new URL(document.referrer).origin !== window.location.origin) {
                  window.location.assign(document.referrer);
                  return;
                }

                navigate('/login');
              }}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={styles.submitButton}
              disabled={isSubmitting || !!pendingValues}
            >
              Зарегистрироваться
            </Button>
          </div>
        </form>
      </div>
      {pendingValues && <CaptchaDialog onClose={() => setPendingValues(null)} onConfirm={confirmRegistration} />}
    </div>
  );
};

export default DemoRegistration;
